import { forwardRef } from "react";
import PropTypes from "prop-types";
import ButtonBase from "@mui/material/ButtonBase";
import Box from "@mui/material/Box";
import { keyframes } from "@mui/system";
import { coin } from "src/assets";
import { gr, tokens } from "../tokens";

// The member's coins (top bar): the app's coin image and the count, no box around them. The coin hops and
// turns once every few seconds and the count pops when it changes (e.g. after sending coins); both are
// transform-only (no layout shift) and off with "reduce motion". With `to` / `onClick` it is a
// link / button named "{label}: {count}"; pass `component={RouterLink}` with `to` for a route.
const TOUCH = "@media (pointer: coarse), (max-width: 899.95px)";
// One hop-and-turn (about 1 s) at the end of each cycle, then still.
const TURN_CYCLE = "6s";

const turn = keyframes`
  0%, 84% { transform: translateY(0) rotateY(0deg); }
  90% { transform: translateY(-3px) rotateY(180deg); }
  96% { transform: translateY(0) rotateY(330deg); }
  100% { transform: translateY(0) rotateY(360deg); }
`;
const pop = keyframes`
  0% { transform: scale(1); }
  40% { transform: scale(1.18); }
  100% { transform: scale(1); }
`;

const chipSx = {
  display: "inline-flex",
  alignItems: "center",
  gap: gr.space[2],
  height: gr.control.md,
  px: gr.space[2],
  borderRadius: gr.radius.control,
  color: gr.color.ink,
  fontFamily: gr.font.family,
  fontSize: gr.font.size.md,
  fontWeight: gr.font.weight.semibold,
  lineHeight: 1,
  whiteSpace: "nowrap",
  transition: "background-color .15s ease",
  // Plain on the bar; a soft tint only on hover, and the focus ring for keyboard users.
  "&:hover": { background: gr.color.tint },
  "&.Mui-focusVisible": { boxShadow: gr.shadow.focus },
  [TOUCH]: { height: gr.control.touch },
  "& .gr-coin": {
    // !important only to beat the legacy phone rule `header img { width: 100% !important }`
    // (src/assets/css/style.css), which stretched the coin to 60px in the top bar.
    width: `${gr.icon.lg} !important`,
    height: gr.icon.lg,
    flex: "none",
    animation: `${turn} ${TURN_CYCLE} ease-in-out infinite`,
  },
  "& .gr-coin-count": {
    display: "inline-block",
    fontVariantNumeric: "tabular-nums",
    animation: `${pop} .4s ease-out`,
  },
  "@media (prefers-reduced-motion: reduce)": {
    "& .gr-coin, & .gr-coin-count": { animation: "none" },
  },
};

const formatCount = (value) => Math.round(value || 0).toLocaleString("en-IE");

const GrCoinChip = forwardRef(function GrCoinChip(
  { value, label = "Total Coins", sx, ...rest },
  ref
) {
  const count = formatCount(value);
  return (
    <ButtonBase ref={ref} aria-label={`${label}: ${count}`} sx={{ ...chipSx, ...sx }} {...rest}>
      <Box
        component="img"
        className="gr-coin"
        src={coin}
        alt=""
        aria-hidden="true"
        width={tokens.icon.lg}
        height={tokens.icon.lg}
      />
      {/* A new key restarts the pop whenever the count changes. */}
      <Box component="span" className="gr-coin-count" key={count} aria-hidden="true">
        {count}
      </Box>
    </ButtonBase>
  );
});

GrCoinChip.propTypes = {
  /** The member's coins (total_points). */
  value: PropTypes.number,
  /** Name for screen readers, before the count. */
  label: PropTypes.string,
  sx: PropTypes.object,
};

export default GrCoinChip;
